/**
 * The figures a job card shows, and the two helpers that read them.
 *
 * LIFTED OUT OF `JobCard` SO THE SKU CARD CANNOT DRIFT FROM IT. Home shows the same seven
 * figures whether the page is grouped by order or by product — only what sits in the left-hand
 * column changes — so a second card defining its own labels, hints or dimming rules would be
 * two sources for one vocabulary, and the first divergence would look like a data bug rather
 * than a copy-paste.
 *
 * NO JSX HERE, deliberately: a module exporting both components and plain values breaks fast
 * refresh. `ColumnHeader` and `NumberCell` live next door in `CardFigures.tsx` for that reason,
 * the same split `utils/rowTree.ts` already makes.
 */

/** `dd-MMM`, because a due date is read against today and the year is rarely the question. */
export function formatDue(value: string | null): string {
    if (!value) return 'no date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`;
}

/** The columns, in the order a unit travels. One source, so header and cells cannot drift. */
export const COLUMNS = [
    /*
     * `dimWhenZero` is off for the two figures that are the job itself: an order of 0 or nothing
     * yet made is the thing to look at, not the thing to fade out.
     */
    {
        key: 'ordered',
        label: 'Ordered',
        hint: 'What the brand asked this site to make.',
        dimWhenZero: false,
    },
    {
        key: 'open',
        label: 'Open',
        dimWhenZero: true,
        hint: 'Ordered minus what has been made. Anything rejected at QC or gone out of date is not counted as made, so those units are still owed',
    },
    {
        key: 'manufactured',
        label: 'Manufactured',
        hint: 'Produced against this order and still able to fill it — output less anything rejected at QC or past its expiry',
        dimWhenZero: false,
    },
    {
        key: 'qcApproved',
        label: 'QC approved',
        dimWhenZero: true,
        hint: 'Passed QC and still at the manufacturer site. Once it ships it moves to Sent',
    },
    {
        key: 'qcPending',
        label: 'QC pending',
        dimWhenZero: true,
        hint: 'Manufactured products that are still in QC and still at the manufacturer site. These can still be dispatched - the brand inspects the load on arrival',
    },
    {
        key: 'sent',
        label: 'Sent',
        dimWhenZero: true,
        hint: 'Dispatched from the Warehouse',
    },
    {
        key: 'toSend',
        label: 'To send',
        dimWhenZero: true,
        hint: 'Ordered minus what has left the site. Goods made and still standing here are counted here, not as done',
    },
] as const;

export type ColumnKey = (typeof COLUMNS)[number]['key'];

/**
 * How many figure columns a card lays out.
 *
 * Exported because `JobCard.module.css` has to hard-code the same number in a
 * `grid-template-columns`, and CSS cannot read this array. The two went out of step once
 * already — a seventh column was added here and the grid stayed at six, which wrapped the last
 * figure onto a line of its own — so the constant names the coupling rather than leaving it to
 * be noticed on screen.
 */
export const COLUMN_COUNT = COLUMNS.length;

/** The allocation figure behind a given column, zero for columns shelf stock cannot feed. */
export const fromStockFor = (
    alloc: { total: number; approved: number; pending: number } | null | undefined,
    key: string,
): number =>
    key === 'manufactured'
        ? (alloc?.total ?? 0)
        : key === 'qcApproved'
          ? (alloc?.approved ?? 0)
          : key === 'qcPending'
            ? (alloc?.pending ?? 0)
            : 0;
